/**
 * Dynamic Section Registry System
 * 
 * This module handles the registration and loading of dynamic sections
 * that can be used in the ThankYou page builder.
 */

// Registry to store loaded sections
const sectionRegistry = new Map();

/**
 * Load all available dynamic sections from the sections directory
 */
export const loadDynamicSections = async () => {
  try {
    // Define available section categories and their templates
    const sectionTemplates = [
      { category: 'about-us', template: '1', path: './components/PageRender/sections/about-us/1.js' },
      { category: 'testimonials', template: '1', path: './components/PageRender/sections/testimonials/1.js' },
      { category: 'features', template: '1', path: './components/PageRender/sections/features/1.js' },
      { category: 'hero-landing', template: '1', path: './components/PageRender/sections/hero-landing/1.js' },
      { category: 'layout', template: '1', path: './components/PageRender/sections/layout/1.js' },
      { category: 'custom-html', template: '1', path: './components/PageRender/sections/custom-html/1.js' },
      // Add more sections here as you create them
    ];

    for (const section of sectionTemplates) {
      try {
        // Dynamically import the section
        const module = await import(`../components/PageRender/sections/${section.category}/${section.template}.js`);
        const sectionFunction = module.default || module[Object.keys(module)[0]];
        
        if (typeof sectionFunction === 'function') {
          let sectionSettings = null;
          
          // First, check if the module exports a config directly (for sections with hooks)
          // Look for exports like 'layoutSectionConfig', 'testimonialsSectionConfig', etc.
          // Convert hyphenated names to camelCase (e.g., 'custom-html' -> 'customHtml')
          const camelCaseCategory = section.category.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
          const configKey = `${camelCaseCategory}SectionConfig`;
          if (module[configKey]) {
            sectionSettings = module[configKey];
          } else {
            // Fallback: Extract settings by calling the function with a mock getSettings
            const mockGetSettings = (settings) => {
              sectionSettings = settings;
            };
            
            // Call the section function to extract settings
            try {
              sectionFunction(() => ({}), {}, mockGetSettings);
            } catch (e) {
              // Ignore render errors, we just want the settings
              console.warn(`Could not extract settings for ${section.category}/${section.template}:`, e.message);
            }
          }
          
          if (sectionSettings) {
            const sectionId = `${section.category}-${section.template}`;
            sectionRegistry.set(sectionId, {
              id: sectionId,
              category: section.category,
              template: section.template,
              label: sectionSettings.label || formatSectionLabel(section.category, section.template),
              description: sectionSettings.description || `${section.category} section template ${section.template}`,
              icon: sectionSettings.icon || 'Layout', // Default icon
              settings: sectionSettings,
              renderFunction: sectionFunction,
              defaultContent: generateDefaultContent(sectionSettings)
            });
          }
        }
      } catch (error) {
        console.warn(`Failed to load section ${section.category}/${section.template}:`, error);
      }
    }
    
    console.log(`Loaded ${sectionRegistry.size} dynamic sections`);
    return Array.from(sectionRegistry.values());
  } catch (error) {
    console.error('Failed to load dynamic sections:', error);
    return [];
  }
};

/**
 * Get a specific section by ID
 */
export const getDynamicSection = (sectionId) => {
  return sectionRegistry.get(sectionId);
};

/**
 * Get all loaded dynamic sections
 */
export const getAllDynamicSections = () => {
  return Array.from(sectionRegistry.values());
};

/**
 * Generate default content based on section settings
 */
const generateDefaultContent = (settings) => {
  const defaultContent = {
    sectionBackgroundColor: 'transparent',
    marginTop: '0',
    marginBottom: '24',
    paddingTop: '0',
    paddingBottom: '0',
    paddingLeft: '0',
    paddingRight: '0',
    textAlign: 'center'
  };

  // Handle new columnStructure pattern
  if (settings.columnStructure) {
    const structure = settings.columnStructure;
    const defaultColumnCount = settings.fields?.find(f => f.key === 'columnCount')?.default || 
                               settings.fields?.find(f => f.key === 'featuresPerRow')?.default || '2';
    
    // Create default columns based on the structure
    const columnCount = parseInt(defaultColumnCount);
    defaultContent.actualColumns = [];
    
    for (let i = 0; i < Math.min(columnCount, structure.maxColumns || 3); i++) {
      const column = {
        id: `column-${i}`
      };
      
      // Add default values for each field in the column structure
      structure.fields.forEach(field => {
        column[field.key] = field.default || '';
        
        // Add style defaults based on possible_styles
        if (field.possible_styles) {
          field.possible_styles.forEach(style => {
            column[`${field.key}${style.charAt(0).toUpperCase() + style.slice(1)}`] = getDefaultStyleValue(style);
          });
        }
      });
      
      defaultContent.actualColumns.push(column);
    }
  }

  // Handle legacy columns-based content (for backward compatibility)
  if (settings.columns && !settings.columnStructure) {
    defaultContent.columns = settings.columns.map((column, index) => ({
      id: `column-${index}`,
      type: column.type,
      label: column.label,
      ...generateDefaultForColumn(column),
      // Add style defaults based on possible_styles
      ...(column.possible_styles || []).reduce((acc, style) => {
        acc[style] = getDefaultStyleValue(style);
        return acc;
      }, {})
    }));
  }

  // Handle fields-based content
  if (settings.fields) {
    settings.fields.forEach(field => {
      defaultContent[field.key] = field.default || '';
      // Add style defaults
      if (field.possible_styles) {
        field.possible_styles.forEach(style => {
          defaultContent[`${field.key}_${style}`] = getDefaultStyleValue(style);
        });
      }
    });
  }

  return defaultContent;
};

/**
 * Generate default content for a column
 */
const generateDefaultForColumn = (column) => {
  const defaults = {
    header: { title: column.default || 'Header Title' },
    text: { text: column.default || 'Text content' },
    image: { src: '', alt: column.default || 'Image' },
    button: { text: column.default || 'Click Me', url: '' }
  };
  
  return defaults[column.type] || { content: column.default || '' };
};

/**
 * Get default value for a style property
 */
const getDefaultStyleValue = (style) => {
  const defaults = {
    fontSize: '16',
    fontWeight: 'normal',
    color: '#1f2937',
    backgroundColor: 'transparent',
    textAlign: 'center',
    borderRadius: '8',
    marginTop: '0',
    marginBottom: '16',
    paddingTop: '0',
    paddingBottom: '0'
  };
  
  return defaults[style] || '';
};

/**
 * Format section label from category and template
 */
const formatSectionLabel = (category, template) => {
  const categoryFormatted = category.split('-').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
  
  return `${categoryFormatted} ${template}`;
};

/**
 * Check if a section type is dynamic
 */
export const isDynamicSection = (sectionType) => {
  return sectionRegistry.has(sectionType);
};

/**
 * Render a dynamic section
 * @param {string} sectionType - Section type ID
 * @param {Object} content - Section content/props
 * @param {Function} getContentStyles - Style getter function
 * @param {Object} [editingState] - Editing state for interactive mode
 * @param {Function} [onQuickUpdate] - Update callback
 * @param {string} [previewBreakpoint='desktop'] - Current preview breakpoint for responsive rendering
 */
export const renderDynamicSection = (sectionType, content, getContentStyles, editingState = null, onQuickUpdate = null, previewBreakpoint = 'desktop') => {
  const section = sectionRegistry.get(sectionType);
  if (!section) {
    console.warn(`Dynamic section ${sectionType} not found`);
    return null;
  }

  try {
    // Create a wrapper that mimics the contentWrapper from ThankYou.js
    const contentWrapper = (children) => (
      <div style={{ textAlign: content.textAlign || 'center' }}>
        {children}
      </div>
    );

    // Extend editingState to include previewBreakpoint for responsive preview
    const extendedEditingState = editingState ? {
      ...editingState,
      previewBreakpoint,
    } : { previewBreakpoint };

    // Call the section's render function with optional editing support
    const result = section.renderFunction(getContentStyles, content, () => {}, extendedEditingState, onQuickUpdate);
    
    // Wrap the result if it's not already wrapped
    return contentWrapper(result);
  } catch (error) {
    console.error(`Error rendering dynamic section ${sectionType}:`, error);
    return (
      <div style={{ 
        border: '2px dashed #ef4444', 
        borderRadius: '8px', 
        padding: '16px', 
        color: '#ef4444',
        textAlign: 'center'
      }}>
        Error rendering section: {sectionType} - Please contact support at support@thecustomform.com
      </div>
    );
  }
};